import type { ComputedRef, Ref } from 'vue';
import { head } from 'lodash-es';
import {
  computed,

  ref,

} from 'vue';

import type {
  FormFlattenKey,
  FormFlattenValue,
  FormGroup,
  FormResult,
  FormSchema,
} from '@modules/forms/components/VForm/types';
import type { IFormPlainDataListboxPropsChangeState, IFormSelectInputOption } from '@modules/forms/types';
import type {
  IReportColumnPayload,
  IReportFieldsPayload,
  IReportFieldsResponse,
  IRolesNamespaces,
} from '@modules/roles-and-permissions/types';
import { EFormInputTypes } from '@modules/forms/enums';
import { usePermissionsStore } from '@modules/permissions/store';
import { useRolesAndPermissionsTranslate } from '@modules/roles-and-permissions/composables';
import { ROLES_PERMISSIONS } from '@modules/roles-and-permissions/constants';
import { useAddReportColumnMutation, useRemoveReportColumnMutation } from '@modules/roles-and-permissions/mutations';
import {
  useListReportNamesQuery,
  useReportFieldsQuery,
  useRolesAndTreeQuery,
} from '@modules/roles-and-permissions/queries';
import { watchImmediate } from '@vueuse/core';

import type {
  IEditRoleForm,
  IEditRoleFormEmits,
} from '../types';

export interface RoleEditFormComposable {
  isAddColumnLoading: Ref<boolean>;
  isRemoveColumnLoading: Ref<boolean>;
  isReportFieldsPermitted: ComputedRef<boolean>;
  reportFieldsPayload: ComputedRef<IReportFieldsPayload | undefined>;
  isReportNamesLoading: Ref<boolean>;
  isReportFieldsLoading: Ref<boolean>;
  formSchema: ComputedRef<FormSchema<IEditRoleForm>>;
  groups: ComputedRef<FormGroup<IEditRoleForm>[]>;
  reportFieldsData: Ref<IReportFieldsResponse | undefined>;
  formValues: Ref<IEditRoleForm>;
  translate(shortCode: string): string;
  handleFormChange<T extends IEditRoleForm, K extends FormFlattenKey<T>>(key: K | undefined, value: FormFlattenValue<T, K>, result: FormResult<T>): void;
  handleReportColumnsChange(state: IFormPlainDataListboxPropsChangeState<string>): void;
}

export default function useRoleEditForm(
  namespace: Ref<IRolesNamespaces>,
  createdRoleId: Ref<number | undefined>,
  emit: IEditRoleFormEmits,
): RoleEditFormComposable {
  const { hasPermission } = usePermissionsStore();
  const { translate } = useRolesAndPermissionsTranslate();

  const { mutate: addColumn, isLoading: isAddColumnLoading } = useAddReportColumnMutation();
  const { mutate: removeColumn, isLoading: isRemoveColumnLoading } = useRemoveReportColumnMutation();

  const formValues = ref<IEditRoleForm>({
    roleId: undefined,
    reportName: undefined,
  });

  const reportFieldsPayload = computed<IReportFieldsPayload | undefined>(() => {
    if (!(formValues.value.roleId && formValues.value.reportName)) {
      return undefined;
    }

    return {
      id: formValues.value.roleId,
      report_name: formValues.value.reportName,
      namespace: 'role',
    };
  });

  const isReportFieldsPermitted = computed(() => hasPermission(ROLES_PERMISSIONS.REPORT_FIELDS).value);

  const {
    data: reportFieldsData,
    isFetching: isReportFieldsLoading,
  } = useReportFieldsQuery(reportFieldsPayload, isReportFieldsPermitted);

  const {
    data: reportNamesData,
    isFetching: isReportNamesLoading,
  } = useListReportNamesQuery(isReportFieldsPermitted);

  const { data: rolesAndTreeData, isFetching: isRolesAndTreeLoading } = useRolesAndTreeQuery(namespace);

  const roles = computed<IFormSelectInputOption<number>[]>(() => (
    rolesAndTreeData.value
      ? rolesAndTreeData.value.list.map(({ id, name }) => ({ value: id, label: name }))
      : []
  ));

  const listReportNames = computed<IFormSelectInputOption<string>[]>(() => (
    reportNamesData.value && namespace.value
      ? reportNamesData.value?.[namespace.value].map((val) => ({ value: val, label: translate(val) }))
      : []
  ));

  watchImmediate(listReportNames, () => {
    formValues.value.reportName = head(listReportNames.value)?.value;
  });

  watchImmediate(roles, () => {
    formValues.value.roleId = undefined;
    emitRoleId();
  });

  watchImmediate(createdRoleId, (val) => {
    if (val) {
      formValues.value.roleId = val;
      emitRoleId();
    }
  });

  const formSchema = computed<FormSchema<IEditRoleForm>>(() => ({
    roleId: {
      type: EFormInputTypes.SELECT,
      label: translate('EDIT_CHOOSE_ROLE'),
      options: roles.value,
      value: formValues.value.roleId,
      loading: isRolesAndTreeLoading.value,
    },
    reportName: {
      type: EFormInputTypes.SELECT,
      label: translate('EDIT_FIELDS_SETTINGS_TYPE'),
      options: listReportNames.value,
      value: formValues.value.reportName,
    },
  }));

  const groups = computed<FormGroup<IEditRoleForm>[]>(() => {
    const res: FormGroup<IEditRoleForm>[] = [{
      items: ['roleId'],
    }];

    if (reportFieldsPayload.value && isReportFieldsPermitted.value && !isReportNamesLoading.value) {
      const reportPart: FormGroup<IEditRoleForm> = {
        items: ['reportName'],
        isLoading: isReportFieldsLoading.value,
      };

      res.push(reportPart);
    }

    return res;
  });

  function handleFormChange<T extends IEditRoleForm, K extends FormFlattenKey<T>>(
    key: K | undefined,
    value: FormFlattenValue<T, K>,
    result: FormResult<T>,
  ): void {
    formValues.value = { ...result.values };
    emitRoleId();
  }

  function handleReportColumnsChange(
    state: IFormPlainDataListboxPropsChangeState<string>,
  ): void {
    if (!reportFieldsPayload.value)
      return;

    const { value, search, check } = state;

    const payload: IReportColumnPayload = {
      value,
      search,
      ...reportFieldsPayload.value,
    };

    if (check) {
      addColumn(payload);
    } else {
      removeColumn(payload);
    }
  }

  function emitRoleId(): void {
    emit('update-role', formValues.value.roleId);
  }

  return {
    isAddColumnLoading,
    isRemoveColumnLoading,
    isReportFieldsPermitted,
    reportFieldsPayload,
    isReportNamesLoading,
    isReportFieldsLoading,
    formSchema,
    groups,
    reportFieldsData,
    formValues,
    translate,
    handleFormChange,
    handleReportColumnsChange,
  };
}
